/**
 * PlateFilters — the control bar for the Plate page: scope (My / Team),
 * person filter (Team only), group-by, search, and a show-done toggle.
 */

import { Search, Users, User, Check } from 'lucide-react';
import type { TaskScope } from '@/hooks/useTasks';
import type { GroupBy } from './PlateListView';

const GROUP_OPTIONS: { key: GroupBy; label: string }[] = [
  { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'assignee', label: 'Assignee' },
  { key: 'entity', label: 'Entity' },
];

interface TeamMemberLite {
  id: string;
  name: string;
}

interface PlateFiltersProps {
  scope: TaskScope;
  setScope: (s: TaskScope) => void;
  groupBy: GroupBy;
  setGroupBy: (g: GroupBy) => void;
  personId: string;
  setPersonId: (id: string) => void;
  showDone: boolean;
  setShowDone: (v: boolean) => void;
  /** Board view always shows its Done column, so the toggle is hidden there. */
  hideShowDone?: boolean;
  search: string;
  setSearch: (v: string) => void;
  team: TeamMemberLite[];
}

export function PlateFilters({
  scope,
  setScope,
  groupBy,
  setGroupBy,
  personId,
  setPersonId,
  showDone,
  setShowDone,
  hideShowDone = false,
  search,
  setSearch,
  team,
}: PlateFiltersProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Scope toggle */}
      <div className="flex gap-1 bg-surface-raised rounded-xl p-1">
        {([
          { key: 'mine' as TaskScope, label: 'My Plate', Icon: User },
          { key: 'team' as TaskScope, label: 'Team', Icon: Users },
        ]).map(({ key, label, Icon }) => {
          const active = scope === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setScope(key)}
              className={`px-3 py-2.5 sm:py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                active
                  ? 'bg-brand-secondary text-brand-primary shadow-md'
                  : 'text-theme-muted hover:text-theme-secondary'
              }`}
              aria-pressed={active}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          );
        })}
      </div>

      {/* Person filter (Team only) */}
      {scope === 'team' && (
        <select
          value={personId}
          onChange={(e) => setPersonId(e.target.value)}
          className="text-xs font-medium bg-surface-raised text-theme-secondary rounded-xl px-3 py-2 border border-theme-soft focus:outline-none focus:ring-2 focus:ring-brand-secondary/40"
        >
          <option value="">Everyone</option>
          {team.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      )}

      {/* Group-by */}
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-theme-muted font-medium hidden sm:inline">Group</span>
        <div className="flex gap-1 bg-surface-raised rounded-xl p-1">
          {GROUP_OPTIONS.map(({ key, label }) => {
            const active = groupBy === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setGroupBy(key)}
                className={`px-2.5 py-2.5 sm:py-1.5 text-[11px] font-semibold rounded-lg transition-all ${
                  active
                    ? 'bg-surface-card text-theme-primary shadow-sm ring-1 ring-theme-soft'
                    : 'text-theme-muted hover:text-theme-secondary'
                }`}
                aria-pressed={active}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-1.5 bg-surface-raised rounded-xl px-3 py-2 ring-1 ring-theme-soft focus-within:ring-2 focus-within:ring-brand-secondary/40 flex-1 min-w-[140px]">
        <Search className="w-3.5 h-3.5 text-theme-muted shrink-0" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tasks…"
          className="flex-1 min-w-0 bg-transparent text-xs text-theme-primary placeholder:text-theme-muted focus:outline-none"
        />
      </div>

      {/* Show done (list only — the board always shows its Done column) */}
      {!hideShowDone && (
        <button
          type="button"
          onClick={() => setShowDone(!showDone)}
          className={`flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold rounded-xl transition-all ${
            showDone
              ? 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-400/20'
              : 'bg-surface-raised text-theme-muted hover:text-theme-secondary'
          }`}
          aria-pressed={showDone}
        >
          <Check className="w-3.5 h-3.5" />
          Done
        </button>
      )}
    </div>
  );
}
